import { Component, ElementRef, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DomainData } from 'src/app/model/domain-data';
import { Ndg, NdgRequest } from 'src/app/model/ndg';
import { City } from 'src/app/model/property';
import { isFisical, isGiuridical, ndgInsertFields, Type } from 'src/app/model/types';
import { MessageService } from 'src/app/service/message.service';
import { NdgService } from 'src/app/service/ndg.service';
import { PropertiesService } from 'src/app/service/properties.service';
import { SpinnerService } from 'src/app/shared/overlay-spinner/service/new-spinner.service';
import { formatDate } from 'src/app/utils/common.functions';
import { fiscalCodeValidator, vatNumberValidator, cityRequired } from 'src/app/utils/validators';

@Component({
  templateUrl: './ndg-insert.component.html',
  styleUrls: ['./ndg-insert.component.scss']
})
export class NdgInsertComponent implements OnInit {
  formGroup!: FormGroup;

  cityList!: City[];
  cityListDomain!: DomainData[];
  typologyList!: DomainData[];
  employeesList!: DomainData[];
  
  disabled = false;
  loading = false;

  constructor(
    private propertiesService: PropertiesService,
    private spinnerService: SpinnerService,
    private elementRef: ElementRef,
    private ndgService: NdgService,
    private messageService: MessageService,
    private translateService: TranslateService
  ) {}
  
  ngOnInit(): void {
    this.spinnerService.showProgress(this.elementRef);

    this.typologyList = ndgInsertFields.type;
    this.translateService.get(this.typologyList.map(i => i.value)).subscribe({
      next: (translate: any) => {
        this.typologyList.forEach(i => i.value = translate[i.value]);
      }
    });

    this.employeesList = ndgInsertFields.employees;
    this.translateService.get(this.employeesList.map(i => i.value)).subscribe({
      next: (translate: any) => {
        this.employeesList.forEach(i => i.value = translate[i.value]);
      }
    });

    this.propertiesService.getCityList().subscribe({
      next: (data: City[]) => {
        this.cityList = data;
        this.cityListDomain = data.map(i => {return {key: i.id, value: i.description} as DomainData});

        this.spinnerService.detach(this.elementRef);
      },
      error: () => {
        this.spinnerService.detach(this.elementRef);
      }
    });

    this.formGroup = new FormGroup({
      type: new FormControl(null, Validators.required),
      description: new FormControl('', Validators.required),
      vatNumber: new FormControl('', [Validators.required, vatNumberValidator()]),
      fiscalCode: new FormControl('', [Validators.required, fiscalCodeValidator()]),

      name: new FormControl('', Validators.required),
      surname: new FormControl('', Validators.required),
      city: new FormControl('', cityRequired()),
      cap: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(5)]),
      address: new FormControl('', Validators.required),
      email: new FormControl('', Validators.email),
      phone: new FormControl(''),
      employees: new FormControl(null, Validators.required),
      dateStartActivity: new FormControl(null, Validators.required)
    });

    this.type?.valueChanges.subscribe({
      next: (type: Type) => {
        if(this.isGiuridical(type)) {
          this.description?.enable();
          this.vatNumber?.enable();
          this.dateStartActivity?.enable();
          this.employees?.enable();
        } else {
          this.description?.disable();
          this.vatNumber?.disable();
          this.dateStartActivity?.disable();
          this.employees?.disable();
        }

        this.formGroup.updateValueAndValidity();
      }
    });
  }

  get type(): AbstractControl | null { return this.formGroup.get('type'); }
  get description(): AbstractControl | null { return this.formGroup.get('description'); }
  get vatNumber(): AbstractControl | null { return this.formGroup.get('vatNumber'); }
  get fiscalCode(): AbstractControl | null { return this.formGroup.get('fiscalCode'); }
  
  get name(): AbstractControl | null { return this.formGroup.get('name'); }
  get surname(): AbstractControl | null { return this.formGroup.get('surname'); }
  get city(): AbstractControl | null { return this.formGroup.get('city'); }
  get cap(): AbstractControl | null { return this.formGroup.get('cap'); }
  get address(): AbstractControl | null { return this.formGroup.get('address'); }
  get email(): AbstractControl | null { return this.formGroup.get('email'); }
  get phone(): AbstractControl | null { return this.formGroup.get('phone'); }
  get dateStartActivity(): AbstractControl | null { return this.formGroup.get('dateStartActivity'); }
  get employees(): AbstractControl | null { return this.formGroup.get('employees'); }

  onCitySelected(selectedCity: DomainData): void {
    let city = this.cityList.find(i => i.id === selectedCity.key);
    this.cap?.setValue(city?.cap.replace(/x/g, ''));
  }

  hasError(field: AbstractControl): boolean {
    return field.invalid && field.touched && this.formGroup.touched;
  }

  isFisical(type: Type): boolean {
    return isFisical(type);
  }

  isGiuridical(type: Type): boolean {
    return isGiuridical(type);
  }

  onSubmit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAsTouched();
      this.formGroup.markAllAsTouched();
      return;
    }

    let payload: NdgRequest;

    if(this.isGiuridical(this.type?.value)) {
      payload = {
        type: this.type?.value,
        customerId: null,

        description: this.description?.value,
        vatNumber: this.vatNumber?.value,
        city: this.city?.value.key,
        cap: this.cap?.value,
        address: this.address?.value,
  
        name: this.name?.value,
        surname: this.surname?.value,
        fiscalCode: this.fiscalCode?.value.toUpperCase(),
        dateStartActivity: formatDate(this.dateStartActivity?.value),

        email: null,
        phone: null,
        employees: this.employees?.value,
      };
    } else {
      // Is fisical

      payload = {
        type: this.type?.value,
        customerId: null,
  
        name: this.name?.value,
        surname: this.surname?.value,
        fiscalCode: this.fiscalCode?.value.toUpperCase(),
        city: this.city?.value.key,
        cap: this.cap?.value,
        address: this.address?.value,
        email: this.email?.value,
        phone: this.phone?.value,

        description: null,
        vatNumber: null,
        dateStartActivity: null,
        employees: null
      }
    }

    this.formGroup.disable();
    this.loading = true;
    this.disabled = true;
    
    this.ndgService.insertNewNdg(payload).subscribe({
      next: (response: Ndg) => {
        this.messageService.success('ndg.insert.success', {ndg: response.ndg})
        this.loading = false;
      },
      error: () => {
        this.messageService.error('ndg.insert.error')

        this.loading = false;
        this.disabled = false;
        this.formGroup.enable();
      }
    });
  }
}
